import type { inferOutput } from '@trpc/tanstack-react-query';
import type { useTRPC } from '../../../lib/trpc.js';

type Trpc = ReturnType<typeof useTRPC>;
export type LoadedRun = inferOutput<Trpc['workflows']['runs']>['runs'][number];

export interface RunLink {
  runId: string;
  actionType: string;
  workflow: string;
}

export interface RunOutcome {
  runId: string;
  kind: string;
  title: string;
}

export interface WorkflowRunRow extends LoadedRun {
  chainedFrom: RunLink | null;
  chainedInto: RunLink[];
  // What this run and everything chained under it saved.
  outcomes: RunOutcome[];
}

export interface RunFilters {
  search: string;
  workflow: string | null;
  owner: string | null;
  starredOnly: boolean;
}

function linkOf(run: LoadedRun): RunLink {
  return { runId: run.runId, actionType: run.actionType, workflow: run.workflow };
}

// Joined over every loaded page, so a chain split across pages links up once both
// halves are in. A run pointing at itself is not its own parent.
export function linkRuns(runs: LoadedRun[]): WorkflowRunRow[] {
  const byRun = new Map(runs.map((run) => [run.runId, run]));
  const children = new Map<string, LoadedRun[]>();
  for (const run of runs) {
    if (run.parentRunId === null || run.parentRunId === run.runId) continue;
    const siblings = children.get(run.parentRunId);
    if (siblings === undefined) children.set(run.parentRunId, [run]);
    else siblings.push(run);
  }

  const outcomes = new Map<string, RunOutcome[]>();
  const visiting = new Set<string>();
  const outcomesOf = (run: LoadedRun): RunOutcome[] => {
    const known = outcomes.get(run.runId);
    if (known !== undefined) return known;
    if (visiting.has(run.runId)) return [];
    visiting.add(run.runId);
    const own = run.kind === null ? [] : [{ runId: run.runId, kind: run.kind, title: run.title }];
    const all = [...own, ...(children.get(run.runId) ?? []).flatMap(outcomesOf)];
    outcomes.set(run.runId, all);
    return all;
  };

  return runs.map((run) => {
    const parent =
      run.parentRunId === null || run.parentRunId === run.runId
        ? undefined
        : byRun.get(run.parentRunId);
    return {
      ...run,
      chainedFrom: parent === undefined ? null : linkOf(parent),
      chainedInto: (children.get(run.runId) ?? []).map(linkOf),
      outcomes: outcomesOf(run),
    };
  });
}

function matches(row: WorkflowRunRow, search: string): boolean {
  const wanted = search.trim().toLowerCase();
  if (wanted === '') return true;
  return [row.workflow, row.title, row.owner]
    .filter((part): part is string => part !== null)
    .some((part) => part.toLowerCase().includes(wanted));
}

export function isFiltered(filters: RunFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.workflow !== null ||
    filters.owner !== null ||
    filters.starredOnly
  );
}

export function shownRuns(
  rows: WorkflowRunRow[],
  filters: RunFilters,
  starred: string[],
): WorkflowRunRow[] {
  return rows.filter(
    (row) =>
      matches(row, filters.search) &&
      (filters.workflow === null || row.workflow === filters.workflow) &&
      (filters.owner === null || row.owner === filters.owner) &&
      (!filters.starredOnly || starred.includes(row.runId)),
  );
}

export function choicesOf(
  rows: WorkflowRunRow[],
  of: (row: WorkflowRunRow) => string | null,
  extra: string[] = [],
): string[] {
  return [...new Set([...extra, ...rows.flatMap((row) => of(row) ?? [])])].sort((left, right) =>
    left.localeCompare(right),
  );
}
